import React, { useState, useEffect } from "react";
import axios from "axios";
import { format, addMonths } from "date-fns";
import { tr } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

export default function Costs() {
  const [rates, setRates] = useState({});
  const [periodDays, setPeriodDays] = useState(30);
  const [loading, setLoading] = useState(true);

  // Generate months from Jan 2024 to Jan 2027
  const months = [];
  const start = new Date(2024, 0, 1);
  const end = new Date(2027, 0, 1);
  
  let curr = start;
  while (curr <= end) {
    months.push(format(curr, "yyyy-MM"));
    curr = addMonths(curr, 1);
  }

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/costs/settings`);
        setRates(data.interest_rates || {});
        setPeriodDays(data.interest_period_days || 30);
      } catch (err) {
        toast.error("Ayarlar çekilemedi.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleRateChange = (month, val) => {
    setRates(prev => ({ ...prev, [month]: parseFloat(val) || 0 }));
  };

  const handleSave = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/costs/settings`, {
        interest_rates: rates,
        interest_period_days: parseInt(periodDays) || 30
      });
      toast.success("Ayarlar başarıyla kaydedildi.");
    } catch (err) {
      toast.error("Ayarlar kaydedilemedi.");
    }
  };

  if (loading) return <div className="p-8 text-center text-neutral-500">Yükleniyor...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      <div className="flex justify-between items-center shrink-0">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Maliyet Ayarları</h1>
        <Button onClick={handleSave} className="gap-2">
          <Save size={16} /> Kaydet
        </Button>
      </div>

      <div className="bg-white p-6 rounded-lg border shadow-sm space-y-8">
        
        {/* Kesim Günü */}
        <div className="space-y-3 max-w-sm">
          <Label className="text-base font-semibold">Faiz Hesap Kesimi (Gün)</Label>
          <p className="text-sm text-neutral-500">Faiz hesaplamaları kaç günde bir yapılacak?</p>
          <div className="flex items-center gap-3">
            <Input 
              type="number" 
              value={periodDays} 
              onChange={e => setPeriodDays(e.target.value)} 
              className="w-24 font-semibold text-lg"
            />
            <span className="text-neutral-600 font-medium">Günde bir</span>
          </div>
        </div>

        <hr />

        {/* Aylık Faiz Oranları */}
        <div className="space-y-4">
          <div>
            <Label className="text-base font-semibold">Aylık Faiz Oranları (%)</Label>
            <p className="text-sm text-neutral-500 mt-1">2024 Ocak ayından 2027 Ocak ayına kadar geçerli olacak faiz oranlarını giriniz.</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {months.map(m => {
              const label = format(new Date(`${m}-01`), "MMM yyyy", { locale: tr });
              return (
                <div key={m} className="space-y-1">
                  <Label className="text-xs text-neutral-600 capitalize block truncate" title={label}>{label}</Label>
                  <div className="relative">
                    <Input 
                      type="number" 
                      step="0.01" 
                      value={rates[m] === undefined ? "" : rates[m]} 
                      onChange={e => handleRateChange(m, e.target.value)}
                      placeholder="0.00"
                      className="pr-6 text-sm"
                    />
                    <span className="absolute right-2 top-2.5 text-neutral-400 text-xs">%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}
